'use client';
import { fetchPointsMallProductDetail } from '@/api/account';
import { fetchGoodsDetail } from '@/api/product';
import { IFreeData, IPointsMallInfo, IRedeemableItem } from '@/api/types/account';
import { IPrice, IProductDetail, IVipPrice } from '@/api/types/public';
import { handleTopCarouselData, IImageListItem } from '@/components/GoodsDetail/handleData';
import LenseAvailable from '@/components/GoodsDetail/LenseAvailable';
import ProductDescriptionCollapse from '@/components/GoodsDetail/ProductDescriptionCollapse';
import ProductDetailsCollapse from '@/components/GoodsDetail/ProductDetailsCollapse';
import ProductIncluded from '@/components/GoodsDetail/ProductIncluded';
import Price from '@/components/Product/Price';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { colors, font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { formatPriceNumberWithSymbol, genPrice } from '@/utils/price';
import { Box, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import React, { useEffect, useState } from 'react';
import FixedBottomButton from './FixedBottomButton';
import FixedBottomButtonPointsMall from './FixedBottomButtonPointsMall';
import ProductGallery from './ProductGallery';
import RedemptionNotice from './RedemptionNotice';
import SkeletonLoader from './SkeletonLoader';
import { ContentSection, StyledDialog } from './ui-components';

// 定义Props接口
interface RedeemedDetailDialogProps {
  show: boolean;
  onClose: () => void;
  item: IRedeemableItem;
  isRedeemed?: boolean; // 是否为兑换商品
  isPointsMall?: boolean; // 是否为积分商城兑换商品
  freeData?: IFreeData;
  pointsMallData?: IPointsMallInfo;
  refreshFreeData?: () => void; // 刷新兑换券数据
}

/**
 * 商品兑换详情对话框组件
 * 左侧为固定图片，右侧为可滚动内容，底部固定按钮
 */
const RedeemedDetailDialog: React.FC<RedeemedDetailDialogProps> = ({
  show,
  onClose,
  item,
  isRedeemed,
  isPointsMall,
  freeData,
  refreshFreeData,
  pointsMallData,
}) => {
  const t = useTranslations('account.redeemableItems.RedeemedDetailDialog.index');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const currency = useStore((state) => state.currency);
  const [detail, setDetail] = useState<IProductDetail & { point_price_info?: IPrice[] }>();
  const [handledData, setHandledData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [productImages, setProductImages] = useState<IImageListItem[]>([]);
  const { trackEvent } = useReporting();
  useEffectWithCurReady(() => {
    if (show) {
      setLoading(true);
      getDetail();
    } else {
      setDetail(null);
      setProductImages([]);
    }
  }, [show]);

  useEffect(() => {
    if (detail?.id) {
      const _handledData = handleTopCarouselData(detail);
      const { productDetail } = _handledData;
      setProductImages(productDetail?.imageList || []);
      setHandledData(_handledData);
    }
  }, [detail]);

  const getDetail = async () => {
    try {
      if (isPointsMall) {
        const { code, data } = await fetchPointsMallProductDetail(item.shop_sku);
        if (code === 200) {
          setDetail({ ...data, imageList: [] } as any);

          trackEvent(
            {
              eventName: 'PointsMallProductDetailsPage_View',
              properties: {
                product_class_name: data?.products_category,
                is_product_lens: data?.is_glass === 1 && data?.product_lens === 1,
                shop_sku: data?.shop_sku,
                business_sku: (data as IProductDetail)?.business_sku,
                product_sku: data?.sku,
                product_id: `${data?.id}`,
                product_name: data?.name,
              },
            },
            [ProviderType.SENSORS],
          );
        }
      } else {
        const { code, data } = await fetchGoodsDetail(item.shop_sku);
        if (code === 200) {
          setDetail(data.product as IProductDetail);
        }
      }
    } catch (error) {
      logger.log(error);
    } finally {
      setLoading(false);
    }
  };

  const glassesFilter = () => {
    try {
      let word = '';
      if (detail.short_name) {
        if (detail.is_glass === 1) {
          word = ` ${t('glasses')}`;
        }
        if (detail.is_glass === 1 && detail.is_sunglasses === 1) {
          word = ` ${t('sunglasses')}`;
        }
      }
      return word;
    } catch {
      return '';
    }
  };
  return (
    <StyledDialog open={show} onClose={onClose} footer={null}>
      {loading || !detail ? (
        <SkeletonLoader />
      ) : (
        <Box className="relative">
          {/* 左侧固定图片 */}
          <ProductGallery
            images={productImages}
            isGlass={detail?.is_glass === 1}
            productName={detail?.name}
          />

          {/* 右侧可滚动内容 */}
          <ContentSection>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontFamily: font.bb,
                fontSize: '32px',
                mb: 1,
              }}
            >
              {detail?.short_name ? `${detail.short_name}${glassesFilter()}` : ''}
            </Typography>

            <Box className="flex mb-24 gap-16">
              <Typography variant="body2" className="text-14" sx={{ color: colors.auxiliaryText }}>
                {t('sku', { code: detail.shop_sku })}
              </Typography>
              {detail.frame_color && (
                <Typography
                  variant="body2"
                  className="text-14"
                  sx={{ color: colors.auxiliaryText }}
                >
                  {t('color', { value: detail.frame_color })}
                </Typography>
              )}
            </Box>
            {isPointsMall ? (
              <>
                <div className="mb-16 flex items-center font-bb text-16">
                  <span>
                    {detail.point} {detail.point > 1 ? t('points') : t('point')}
                  </span>
                  <span className="notranslate">
                    &nbsp;+
                    {formatPriceNumberWithSymbol(
                      genPrice(detail.point_price_info, currency.name).sell_price,
                      currency,
                    )}
                  </span>
                </div>
              </>
            ) : (
              <Box className="mb-[24px] flex items-center">
                <Typography variant="subtitle1" className="text-16" sx={{ fontFamily: font.bb }}>
                  {t('redeemedItemX1')}
                </Typography>
                <Price
                  priceObject={detail as unknown as IVipPrice}
                  renderPrice={(price, sellPrice) => (
                    <Typography
                      variant="body2"
                      className="text-auxiliary-text text-14"
                      sx={{ ml: 1, color: colors.auxiliaryText, textDecoration: 'line-through' }}
                      aria-label={tPriceA11y('srOriginal') + (price || sellPrice)}
                    >
                      <span className="sr-only">{tPriceA11y('srOriginal')}</span>
                      {price || sellPrice}
                    </Typography>
                  )}
                />
              </Box>
            )}

            {isPointsMall && (
              <div className="mb-24 text-14">
                {t('yourPoints', { points: pointsMallData?.points })}
              </div>
            )}

            {/* redemption notice */}
            {isPointsMall && <RedemptionNotice detail={detail} />}
            {/* lense available */}
            <LenseAvailable detail={detail} />
            {/* product included */}
            {isRedeemed && <ProductIncluded detail={detail} />}
            {/* product details */}
            {detail.is_glass === 1 && <ProductDetailsCollapse detail={detail} />}
            {/* product description */}
            {detail.is_glass === 2 && detail.show_description && (
              <ProductDescriptionCollapse detail={detail} />
            )}
            {/* 底部空间，确保内容可以滚动到底部按钮之上 */}
            <Box className="h-[52px]" />
          </ContentSection>
          {/* 底部固定按钮 */}
          {isPointsMall ? (
            <FixedBottomButtonPointsMall
              detail={detail}
              pointsMallData={pointsMallData}
              refreshFreeData={refreshFreeData}
              // onClose={onClose}
              handledData={handledData}
            />
          ) : (
            <FixedBottomButton
              detail={detail}
              freeData={freeData}
              refreshFreeData={refreshFreeData}
              // onClose={onClose}
            />
          )}
        </Box>
      )}
    </StyledDialog>
  );
};

export default RedeemedDetailDialog;
